'use client'

import type { Learn } from '@/payload-types'
import { LearnFeaturedCard } from './LearnFeaturedCard'
import { useCarouselScroll } from '@/hooks/useCarouselScroll'

interface Props {
  articles: Learn[]
  autoplayDelay?: number
}

export function LearnFeaturedCarousel({ articles, autoplayDelay = 10000 }: Props) {
  const { scrollRef, activeIndex, scrollToIndex, handleKeyDown } = useCarouselScroll({
    itemCount: articles.length,
    autoplayDelay,
    // Dots rather than edge-disabled arrows, and autoplay already cycles.
    loop: true,
  })

  return (
    <div
      className="relative"
      role="region"
      aria-label="New tutorials carousel"
      aria-roledescription="carousel"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={scrollRef}
        role="list"
        className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide"
      >
        {articles.map((article, index) => (
          <div
            key={article.id}
            data-card
            role="listitem"
            aria-label={`Slide ${index + 1} of ${articles.length}`}
            className="w-full shrink-0 snap-start"
          >
            <LearnFeaturedCard article={article} variant="index" />
          </div>
        ))}
      </div>

      {articles.length > 1 && (
        <div
          className="absolute top-6 right-6 flex items-center gap-1 z-10"
          role="group"
          aria-label="Carousel pagination"
        >
          {articles.map((_, index) => (
            <button
              key={index}
              aria-current={index === activeIndex ? 'step' : undefined}
              onClick={() => scrollToIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-3 rounded-full border border-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                index === activeIndex ? 'w-7 bg-white' : 'w-3 bg-transparent'
              }`}
            />
          ))}
        </div>
      )}

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {`Slide ${activeIndex + 1} of ${articles.length}`}
      </div>
    </div>
  )
}
